import { useTippy } from "vue-tippy";

import debounce from "lodash/debounce";

import type { Directive, DirectiveBinding } from "vue";

interface TippyInstance {
  destroy: () => void;
  setContent: (content: string) => void;
}

interface TooltipableElement extends HTMLElement {
  _tooltipInstance?: TippyInstance;
  _mutationObserver?: MutationObserver;
  _previousText?: string;
  _collapsedContent?: string;
  _useCollapsedMode?: boolean;
  _tooltipTarget?: TooltipableElement;
}

const directive: Directive = {
  mounted(el: TooltipableElement, binding: DirectiveBinding) {
    setupTooltip(el, binding);
  },

  updated(el: TooltipableElement, binding: DirectiveBinding) {
    if (binding.modifiers?.collapsed) {
      const target = el._tooltipTarget ?? el;
      target._collapsedContent = binding.value;
    }
  },

  unmounted(el: TooltipableElement) {
    cleanupTooltip(el);
  },
};

const PLACEMENT_MODIFIERS = ["top", "right", "bottom", "left"] as const;

function setupTooltip(el: TooltipableElement, binding: DirectiveBinding): void {
  const theme = binding.modifiers?.themeLight ? "light" : undefined;
  const useCollapsedMode = binding.modifiers?.collapsed ?? false;
  const placement = PLACEMENT_MODIFIERS.find(
    (modifier) => binding.modifiers?.[modifier]
  );

  // If there's a <p> inside, use that instead
  const target = (el.querySelector("p") as TooltipableElement) || el;
  el._tooltipTarget = target;

  target._useCollapsedMode = useCollapsedMode;
  target._collapsedContent = useCollapsedMode ? binding.value : undefined;

  const getCurrentText = () => target.textContent || "";

  const isTextTruncated = () => {
    if (target.className.includes("line-clamp-")) {
      return target.scrollHeight > target.clientHeight;
    }
    return target.scrollWidth > target.clientWidth;
  };

  const getTooltipContent = () => {
    const text = getCurrentText();
    if (!target._useCollapsedMode) {
      return text;
    }
    const collapsed = target._collapsedContent;
    if (!collapsed) {
      return text;
    }
    return isTextTruncated() ? `${text} \u2022 ${collapsed}` : collapsed;
  };

  const tippyInstance = useTippy(target, {
    content: getTooltipContent(),
    theme,
    placement,
    onShow: () => {
      const currentText = getCurrentText();

      if (target._useCollapsedMode) {
        const hasCollapsedContent = !!target._collapsedContent;
        if (!currentText || (!isTextTruncated() && !hasCollapsedContent)) {
          return false;
        }
      } else if (!currentText || !isTextTruncated()) {
        return false;
      }

      tippyInstance?.setContent(getTooltipContent());
    },
    allowHTML: false,
    maxWidth: 400,
  });

  target._tooltipInstance = tippyInstance;
  target._previousText = getCurrentText();

  const observer = new MutationObserver(
    debounce((mutations: MutationRecord[]) => {
      if (
        mutations.some(
          (m) => m.type === "childList" || m.type === "characterData"
        )
      ) {
        tippyInstance?.setContent(getCurrentText());
      }
    }, 300)
  );

  observer.observe(target, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  target._mutationObserver = observer;
}

function cleanupTooltip(el: TooltipableElement): void {
  const target = el._tooltipTarget ?? el;

  if (target._tooltipInstance?.destroy) {
    target._tooltipInstance.destroy();
    target._tooltipInstance = undefined;
  }

  if (target._mutationObserver?.disconnect) {
    target._mutationObserver.disconnect();
    target._mutationObserver = undefined;
  }

  target._previousText = undefined;
  el._tooltipTarget = undefined;
}

export default directive;
